/** Pure helpers for StoryboardArtifact → ShotUnit[] / AssemblyManifest
 * owned by `storyboard_generation` (VISTOERA_STORYBOARD_UNIFIED_VIDEO_
 * ORCHESTRATION + VISTOERA_VIDEO_PRODUCTION_ROUGH_CUT_TIMELINE_PREVIEW_
 * AND_FINAL_ASSEMBLY_CARD_P0_V1). Plain field objects only; HTTP stays
 * in `CanvasBoard.tsx`.
 *
 * Responsibility boundary (spec §2/§3): this module (and the
 * `storyboard_generation` orchestrator it feeds) owns AGGREGATING Shot
 * video results + Rough Cut + Final Assembly ONLY. It never manages
 * keyframes/voice/prompts/single-shot generation — that stays the
 * Advanced Video Generation Card's (`generate_video` node) job.
 *
 * Shot ↔ video-unit binding (spec §17) is stored directly on the
 * orchestrator's `fields.shotBindings` map
 * (`{ [shot_id]: { advancedVideoNodeId } }`) rather than as a typed
 * dataflow edge — a plain persisted field map is simpler and matches
 * how `storyboardArtifactId`/`scriptArtifactId` provenance is already
 * stored elsewhere.
 */

import type { Node } from '@xyflow/react'
import type { ShotArtifact, StoryboardArtifact } from '../../../api'
import type { FlowNodeData } from './types'
import { validShotsFromStoryboard } from './storyboardArtifactValidity'
import type { ShotLikeForTimeline } from './videoProductionTimeline'
import {
  isProductionFormatComplete,
  readProductionFormat,
  resolveDeliveryProductionTarget,
} from './storyboardProductionFormat'

export const STORYBOARD_CONTEXT_EDGE_RELATION = 'DERIVED_FROM'

export function computeStoryboardArtifactFingerprint(storyboard: StoryboardArtifact | null | undefined): string {
  if (!storyboard) return ''
  const shots = Array.isArray(storyboard.shots) ? storyboard.shots : []
  return JSON.stringify({
    storyboard_artifact_id: storyboard.storyboard_artifact_id,
    total_duration_sec: storyboard.total_duration_sec,
    shot_count: shots.length,
    // Per-shot identity + timing + description signature — any Shot's
    // duration/order/content change must be detectable as "the
    // upstream Storyboard changed" even if `storyboard_artifact_id`
    // itself was kept (e.g. an in-place Storyboard edit round-trip).
    shots: shots.map((s) => `${s.shot_id}:${s.shot_index}:${s.duration_sec}:${s.description.length}`),
  })
}

// ---------------------------------------------------------------------
// ShotUnit derivation — Storyboard Shot + bound Advanced Video node
// live snapshot → the canonical shot-list/timeline row.
// ---------------------------------------------------------------------

/** Reuses the EXACT single-shot generation lifecycle vocabulary — never
 * an invented parallel status set (spec §15). */
export type ShotUnitStatus =
  | 'READY_TO_PREP'
  | 'READY_TO_GENERATE'
  | 'GENERATING'
  | 'GENERATED'
  | 'MISSING_VIDEO'
  | 'FAILED'
  | 'FORMAT_MISMATCH'

const GENERATING_STATUSES = new Set(['SUBMITTING', 'QUEUED', 'WAITING_FOR_ENGINE', 'GENERATING'])

/** Read-only snapshot of a bound `generate_video` node's own live
 * fields — built by `CanvasBoard` from its live `nodes` state (this
 * module has no direct graph access itself, same separation as every
 * other pure `*Canvas.ts` module in this folder). */
export interface BoundVideoNodeSnapshot {
  nodeId: string
  /** Raw `generate_video` `fields.status` — 'READY' | 'SUBMITTING' |
   * 'QUEUED' | 'WAITING_FOR_ENGINE' | 'GENERATING' | 'COMPLETED' | 'FAILED'. */
  status?: string | null
  generatedTaskId?: string | null
  generatedVideoUrl?: string | null
  posterUrl?: string | null
  outputDurationSeconds?: number | null
  /** Changes whenever a NEW video finishes generating on this node —
   * used as the per-Shot "video version" for staleness detection.
   * `generatedTaskId` itself is exactly this (a fresh Job id every
   * completed generation), kept as its own named field only for
   * readability at call sites. */
  /** Storyboard production_format.version this video was generated against. */
  generatedAgainstFormatVersion?: number | null
  /** Explicit mismatch flag set when Storyboard format changes after generation. */
  formatCompatibility?: 'ok' | 'format_mismatch' | null
}

export type ShotBindingMap = Record<string, { advancedVideoNodeId: string } | undefined>

/** Shot → generator bindings live on orchestrator `fields.shotBindings`
 * (owner: `storyboard_generation`) — never nested under a context field. */
export function readVideoProductionShotBindings(fields: Record<string, unknown>): ShotBindingMap {
  return (fields.shotBindings || {}) as ShotBindingMap
}

export function ensureVideoProductionShotBinding(
  bindings: ShotBindingMap,
  shotId: string,
  advancedVideoNodeId: string,
): ShotBindingMap {
  const existing = bindings[shotId]
  if (existing?.advancedVideoNodeId === advancedVideoNodeId) return bindings
  return { ...bindings, [shotId]: { advancedVideoNodeId } }
}

export interface ShotGeneratorOwnership {
  nodeId: string
  boundShotId: string
  /** Canonical orchestrator node id (`storyboard_generation`). */
  boundOrchestratorNodeId: string
  /**
   * @deprecated Read-only fallback for one release of tenant data cleanup.
   * Prefer `boundOrchestratorNodeId`. Still accepted when reading legacy
   * `boundVideoProductionNodeId` fields on existing `generate_video` nodes.
   */
  boundVideoProductionNodeId: string
}

export function readShotGeneratorOwnership(node: Node): ShotGeneratorOwnership | null {
  const data = node.data as unknown as FlowNodeData
  if (data.nodeType !== 'generate_video') return null
  const fields = (data.fields || {}) as Record<string, unknown>
  const boundShotId = typeof fields.boundShotId === 'string' ? fields.boundShotId : null
  const boundOrchestratorNodeId =
    (typeof fields.boundOrchestratorNodeId === 'string' ? fields.boundOrchestratorNodeId : null) ||
    (typeof fields.boundVideoProductionNodeId === 'string' ? fields.boundVideoProductionNodeId : null)
  if (!boundShotId || !boundOrchestratorNodeId) return null
  return {
    nodeId: node.id,
    boundShotId,
    boundOrchestratorNodeId,
    boundVideoProductionNodeId: boundOrchestratorNodeId,
  }
}

export type ResolveShotGeneratorResult =
  | { kind: 'bound'; nodeId: string; bindingRepaired: boolean; ambiguousDuplicate: boolean }
  | { kind: 'not_found' }

/** Canonical Shot → `generate_video` resolver for ONE orchestrator +
 * ONE shot id. Uses persisted `shotBindings` first, then falls back to
 * generator ownership metadata on existing Canvas nodes (P0_V3 §七/§八). */
export function resolveCanonicalShotGenerator(
  nodes: Node[],
  orchestratorNodeId: string,
  shotId: string,
  bindings: ShotBindingMap,
): ResolveShotGeneratorResult {
  const bindingEntry = bindings[shotId]
  const candidates: string[] = []
  for (const node of nodes) {
    const ownership = readShotGeneratorOwnership(node)
    if (!ownership) continue
    if (ownership.boundOrchestratorNodeId === orchestratorNodeId && ownership.boundShotId === shotId) {
      candidates.push(node.id)
    }
  }
  const ambiguousDuplicate = candidates.length > 1

  if (bindingEntry?.advancedVideoNodeId) {
    const boundNode = nodes.find((n) => n.id === bindingEntry.advancedVideoNodeId)
    const ownership = boundNode ? readShotGeneratorOwnership(boundNode) : null
    if (
      boundNode &&
      ownership &&
      ownership.boundOrchestratorNodeId === orchestratorNodeId &&
      ownership.boundShotId === shotId
    ) {
      return { kind: 'bound', nodeId: boundNode.id, bindingRepaired: false, ambiguousDuplicate }
    }
  }

  if (candidates.length === 1) {
    return {
      kind: 'bound',
      nodeId: candidates[0],
      bindingRepaired: bindingEntry?.advancedVideoNodeId !== candidates[0],
      ambiguousDuplicate: false,
    }
  }

  if (candidates.length > 1) {
    if (bindingEntry?.advancedVideoNodeId && candidates.includes(bindingEntry.advancedVideoNodeId)) {
      return {
        kind: 'bound',
        nodeId: bindingEntry.advancedVideoNodeId,
        bindingRepaired: false,
        ambiguousDuplicate,
      }
    }
    // Deterministic safe rule: lowest node id — never create a third.
    const canonicalId = candidates.slice().sort()[0]
    return {
      kind: 'bound',
      nodeId: canonicalId,
      bindingRepaired: bindingEntry?.advancedVideoNodeId !== canonicalId,
      ambiguousDuplicate,
    }
  }

  return { kind: 'not_found' }
}

export interface ShotUnit {
  shotId: string
  order: number
  timelineStartSec: number
  timelineEndSec: number
  durationSec: number
  title: string
  description: string
  /** Priority-ordered per spec §11: keyframe/thumbnail still →
   * Storyboard-provided reference. Never the actual generated video
   * asset itself (that's `videoUrl`, used by the Player instead). */
  thumbnailUrl: string | null
  status: ShotUnitStatus
  advancedVideoNodeId: string | null
  /** `generate_video`'s own completed output Job id — this Shot's
   * "video version" for staleness comparisons. */
  videoJobId: string | null
  videoUrl: string | null
  posterUrl: string | null
  durationMismatch: boolean
}

function computeShotUnitStatus(
  binding: { advancedVideoNodeId: string } | undefined,
  bound: BoundVideoNodeSnapshot | undefined,
  currentFormatVersion?: number | null,
): ShotUnitStatus {
  if (!binding || !bound) return 'READY_TO_PREP'
  const status = String(bound.status || 'READY')
  if (status === 'FAILED') return 'FAILED'
  if (GENERATING_STATUSES.has(status)) return 'GENERATING'
  if (status === 'COMPLETED') {
    if (!(bound.generatedTaskId && bound.generatedVideoUrl)) return 'MISSING_VIDEO'
    if (bound.formatCompatibility === 'format_mismatch') return 'FORMAT_MISMATCH'
    if (
      currentFormatVersion != null &&
      bound.generatedAgainstFormatVersion != null &&
      bound.generatedAgainstFormatVersion !== currentFormatVersion
    ) {
      return 'FORMAT_MISMATCH'
    }
    return 'GENERATED'
  }
  // status === 'READY' (or unknown) — a node exists (prepped) but has
  // never produced a completed result.
  return bound.generatedTaskId ? 'MISSING_VIDEO' : 'READY_TO_GENERATE'
}

export function deriveShotUnits(
  storyboard: StoryboardArtifact,
  bindings: ShotBindingMap,
  boundNodesById: Record<string, BoundVideoNodeSnapshot | undefined>,
  currentFormatVersion?: number | null,
): ShotUnit[] {
  const shots = validShotsFromStoryboard(storyboard)
  return shots
    .slice()
    .sort((a, b) => a.shot_index - b.shot_index)
    .map((shot: ShotArtifact) => {
      const binding = bindings[shot.shot_id]
      const bound = binding ? boundNodesById[binding.advancedVideoNodeId] : undefined
      const status = computeShotUnitStatus(binding, bound, currentFormatVersion)
      const durationSec = Math.max(0, Number(shot.duration_sec) || 0)
      const videoUrl = status === 'GENERATED' ? bound?.generatedVideoUrl ?? null : null
      const durationMismatch =
        status === 'GENERATED' && bound?.outputDurationSeconds != null
          ? Math.abs((bound.outputDurationSeconds as number) - durationSec) > 0.35
          : false
      return {
        shotId: shot.shot_id,
        order: shot.shot_index,
        timelineStartSec: Math.max(0, Number(shot.start_time_sec) || 0),
        timelineEndSec: Math.max(0, Number(shot.end_time_sec) || 0),
        durationSec,
        title: shot.title,
        description: shot.description,
        thumbnailUrl: shot.thumbnail_url ?? null,
        status,
        advancedVideoNodeId: binding?.advancedVideoNodeId ?? null,
        videoJobId: status === 'GENERATED' ? bound?.generatedTaskId ?? null : null,
        videoUrl,
        posterUrl: status === 'GENERATED' ? bound?.posterUrl ?? null : null,
        durationMismatch,
      }
    })
}

export function shotUnitsToTimelineShots(units: ShotUnit[]): ShotLikeForTimeline[] {
  return units.map((u) => ({
    shotId: u.shotId,
    order: u.order,
    durationSec: u.durationSec,
    videoUrl: u.videoUrl,
    posterUrl: u.posterUrl ?? u.thumbnailUrl,
    placeholderReason:
      u.status === 'GENERATED'
        ? undefined
        : u.status === 'FAILED'
          ? 'FAILED'
          : u.status === 'GENERATING'
            ? 'GENERATING'
            : u.status === 'MISSING_VIDEO'
              ? 'MISSING_VIDEO'
              : u.status === 'READY_TO_GENERATE'
                ? 'READY_TO_GENERATE'
                : 'READY_TO_PREP',
  }))
}

export interface ShotUnitsSummary {
  total: number
  generatedCount: number
  allRequiredShotsReady: boolean
}

export function summarizeShotUnits(units: ShotUnit[]): ShotUnitsSummary {
  const generatedCount = units.filter((u) => u.status === 'GENERATED').length
  return {
    total: units.length,
    generatedCount,
    allRequiredShotsReady: units.length > 0 && generatedCount === units.length,
  }
}

/** True only when the Shot has a playable generated video URL (not status alone). */
export function hasValidGeneratedShotVideo(unit: ShotUnit): boolean {
  return unit.status === 'GENERATED' && Boolean(unit.videoUrl && String(unit.videoUrl).trim())
}

/**
 * First Shot in Storyboard canonical order with a valid generated video.
 * Pure derived — never persisted as collapsedPosterShotId.
 */
export function resolveFirstAvailableGeneratedShot(units: ShotUnit[]): ShotUnit | null {
  const ordered = [...units].sort((a, b) => a.order - b.order)
  for (const unit of ordered) {
    if (hasValidGeneratedShotVideo(unit)) return unit
  }
  return null
}

export type CollapsedStoryboardPosterKind = 'final_video' | 'first_shot_video' | 'none'

export interface CollapsedStoryboardPoster {
  kind: CollapsedStoryboardPosterKind
  /** Shot used when kind === 'first_shot_video'. */
  shot: ShotUnit | null
  /** Preferred still URL (video asset poster). May be null — caller can use videoUrl. */
  posterUrl: string | null
  /** Playable video URL for lightweight first-frame preview when posterUrl is absent. */
  videoUrl: string | null
  shotIndexLabel: number | null
  timeRangeLabel: string | null
}

function formatCollapsedClock(seconds: number): string {
  const s = Math.max(0, seconds)
  const m = Math.floor(s / 60)
  const sec = Math.floor(s % 60)
  return `${String(m).padStart(2, '0')}:${String(sec).padStart(2, '0')}`
}

/**
 * Collapsed result poster priority (canonical):
 * 1. Current valid final merged video
 * 2. First valid generated Shot video (Storyboard order)
 * 3. Empty
 */
export function resolveCollapsedStoryboardPoster(args: {
  units: ShotUnit[]
  finalVideoArtifact: FinalVideoArtifactSnapshot | null
  finalVideoStale: boolean
  finalVideoUrl?: string | null
}): CollapsedStoryboardPoster {
  const { units, finalVideoArtifact, finalVideoStale, finalVideoUrl } = args
  if (finalVideoArtifact && !finalVideoStale && finalVideoUrl) {
    return {
      kind: 'final_video',
      shot: null,
      posterUrl: null,
      videoUrl: finalVideoUrl,
      shotIndexLabel: null,
      timeRangeLabel: null,
    }
  }
  const first = resolveFirstAvailableGeneratedShot(units)
  if (!first) {
    return {
      kind: 'none',
      shot: null,
      posterUrl: null,
      videoUrl: null,
      shotIndexLabel: null,
      timeRangeLabel: null,
    }
  }
  const ordered = [...units].sort((a, b) => a.order - b.order)
  const index = ordered.findIndex((u) => u.shotId === first.shotId)
  return {
    kind: 'first_shot_video',
    shot: first,
    // Prefer real video-asset poster — never storyboard shot thumbnail as "video result".
    posterUrl: first.posterUrl || null,
    videoUrl: first.videoUrl,
    shotIndexLabel: index >= 0 ? index + 1 : first.order,
    timeRangeLabel: `${formatCollapsedClock(first.timelineStartSec)}–${formatCollapsedClock(first.timelineEndSec)}`,
  }
}

// ---------------------------------------------------------------------
// AssemblyManifest (spec §19/§23/§24) — server-verifiable, built here
// (pure) from live ShotUnits; `CanvasBoard` is the only caller that
// actually POSTs it.
// ---------------------------------------------------------------------

export const ASSEMBLY_PROVIDER_STANDARD_FFMPEG = 'STANDARD_FFMPEG'
export const ASSEMBLY_SKILL_STANDARD_VIDEO_ASSEMBLY = 'STANDARD_VIDEO_ASSEMBLY'

export interface AssemblyManifestShot {
  shot_id: string
  order: number
  expected_duration_sec: number
  video_job_id: string
}

/** Delivery / Final Conform contract stamped from Storyboard Production Format. */
export interface AssemblyDeliveryContract {
  aspect_ratio: string
  resolution: string
  delivery_target_width: number
  delivery_target_height: number
  production_format_version: number
}

export interface AssemblyManifest {
  canvas_node_id: string
  storyboard_artifact_id: string | null
  provider: string
  skill: string
  shots: AssemblyManifestShot[]
  production_format: {
    aspect_ratio: string
    resolution: string
    version: number
  }
  aspect_ratio: string
  delivery_target_width: number
  delivery_target_height: number
}

/** Read Storyboard Production Format → delivery target for Final Conform. */
export function buildAssemblyDeliveryContract(fields: Record<string, unknown>): AssemblyDeliveryContract | null {
  const format = readProductionFormat(fields)
  if (!format || !isProductionFormatComplete(format)) return null
  const target = resolveDeliveryProductionTarget(format)
  if (!target) return null
  return {
    aspect_ratio: format.aspect_ratio,
    resolution: format.resolution,
    delivery_target_width: target.width,
    delivery_target_height: target.height,
    production_format_version: format.version,
  }
}

/** Throws a descriptive Error (never silently builds a partial
 * manifest) if `ALL_REQUIRED_SHOTS_READY` isn't satisfied — spec §22
 * default gate; the button itself should already be disabled, this is
 * the same "assert precondition" convention as
 * `assertStoryboardGeneratePrecondition`. */
export function buildAssemblyManifest(
  nodeId: string,
  storyboardArtifactId: string | null,
  units: ShotUnit[],
  delivery: AssemblyDeliveryContract,
): AssemblyManifest {
  const summary = summarizeShotUnits(units)
  if (!summary.allRequiredShotsReady) {
    throw new Error('NOT_ALL_REQUIRED_SHOTS_READY')
  }
  if (!delivery?.delivery_target_width || !delivery?.delivery_target_height) {
    throw new Error('DELIVERY_FORMAT_MISSING')
  }
  return {
    canvas_node_id: nodeId,
    storyboard_artifact_id: storyboardArtifactId,
    provider: ASSEMBLY_PROVIDER_STANDARD_FFMPEG,
    skill: ASSEMBLY_SKILL_STANDARD_VIDEO_ASSEMBLY,
    production_format: {
      aspect_ratio: delivery.aspect_ratio,
      resolution: delivery.resolution,
      version: delivery.production_format_version,
    },
    aspect_ratio: delivery.aspect_ratio,
    delivery_target_width: delivery.delivery_target_width,
    delivery_target_height: delivery.delivery_target_height,
    shots: units
      .slice()
      .sort((a, b) => a.order - b.order)
      .map((u) => ({
        shot_id: u.shotId,
        order: u.order,
        expected_duration_sec: u.durationSec,
        video_job_id: u.videoJobId as string,
      })),
  }
}

// ---------------------------------------------------------------------
// Final Video Artifact snapshot + staleness (spec §25/§26/§27).
// ---------------------------------------------------------------------

export interface FinalVideoArtifactSnapshot {
  productionId: string
  storyboardId: string | null
  assemblyId: string
  assemblyManifestSnapshot: AssemblyManifest
  /** shot_id → the exact `videoJobId` used at merge time. */
  shotVideoVersions: Record<string, string>
  outputAssetId: string
  durationSeconds: number | null
  provider: string
  skill: string
  createdAt: string
}

export function buildFinalVideoArtifactSnapshot(
  nodeId: string,
  storyboardArtifactId: string | null,
  assembly: { id: string; provider: string; skill: string; duration_seconds: number | null },
  manifest: AssemblyManifest,
): FinalVideoArtifactSnapshot {
  const shotVideoVersions: Record<string, string> = {}
  for (const s of manifest.shots) shotVideoVersions[s.shot_id] = s.video_job_id
  return {
    productionId: nodeId,
    storyboardId: storyboardArtifactId,
    assemblyId: assembly.id,
    assemblyManifestSnapshot: manifest,
    shotVideoVersions,
    outputAssetId: assembly.id,
    durationSeconds: assembly.duration_seconds,
    provider: assembly.provider,
    skill: assembly.skill,
    createdAt: new Date().toISOString(),
  }
}

/** A Shot regenerating (its bound node produced a NEW Job id) makes any
 * existing Final Video Artifact STALE (spec §26) — never auto-deleted,
 * never auto-triggers a re-merge (spec §27, stays a manual action). */
export function isFinalVideoArtifactStale(snapshot: FinalVideoArtifactSnapshot, currentUnits: ShotUnit[]): boolean {
  for (const unit of currentUnits) {
    const snapshotVersion = snapshot.shotVideoVersions[unit.shotId]
    if (snapshotVersion == null) continue // Shot didn't exist in the snapshot — ignore (new Shot added since).
    if (unit.videoJobId !== snapshotVersion) return true
  }
  return false
}
